import { useEffect, useRef, useState } from "react";
import { Maximize2, Minimize2, Download, Edit3, Save, X, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { resolveImageUrl } from "@/components/ImageUpload";
import RichTextEditorPro from "@/components/RichTextEditorPro";

/**
 * Shared magazine-style profile layout used by Institutions, Suppliers and EdTech companies.
 * Layout (based on the reference PDF the user shared):
 *   1. Masthead row — {name} on the left, "{brand} · {year}" on the right
 *   2. Top 3 photos in a 16:9 strip
 *   3. Two magazine columns of rich HTML
 * Everything else (logo, address, contact info, sub-blocks) lives INSIDE the two columns.
 *
 * Ships with three utilities:
 *   • View Full Screen — browser Fullscreen API with larger typography inside FS
 *   • Download PDF     — html2pdf.js snapshot of the whole magazine layout
 *   • Edit Profile     — admin-only inline editor (masthead + photos + both RTE columns),
 *                        persists via `onSave({ ...fields })`.
 */
export default function MagazineProfile({
  photos = [],
  leftHtml = "",
  rightHtml = "",
  claimSlot = null,
  masthead = "",
  brand = "EduTime Today",
  year = new Date().getFullYear(),
  filename = "profile.pdf",
  canEdit = false,       // show "Edit Profile" button when true (admin)
  onSave = null,         // async (fields) => Promise<void>
  testId = "magazine-profile",
}) {
  const rootRef = useRef(null);
  const printableRef = useRef(null);
  const [isFs, setIsFs] = useState(false);
  const [isPdfBusy, setIsPdfBusy] = useState(false);

  // ------ EDIT STATE ------
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [eMasthead, setEMasthead] = useState(masthead);
  const [eBrand, setEBrand]       = useState(brand);
  const [eYear, setEYear]         = useState(String(year || ""));
  const [ePhotos, setEPhotos]     = useState(photos);
  const [eLeft, setELeft]         = useState(leftHtml);
  const [eRight, setERight]       = useState(rightHtml);
  const [busyIdx, setBusyIdx]     = useState(-1);
  const fileRefs = [useRef(null), useRef(null), useRef(null)];

  // Sync in fresh props when the parent record changes (e.g. after save)
  useEffect(() => { setEMasthead(masthead); }, [masthead]);
  useEffect(() => { setEBrand(brand); },       [brand]);
  useEffect(() => { setEYear(String(year || "")); }, [year]);
  useEffect(() => { setEPhotos(photos); },     [photos]);
  useEffect(() => { setELeft(leftHtml); },     [leftHtml]);
  useEffect(() => { setERight(rightHtml); },   [rightHtml]);

  useEffect(() => {
    const on = () => setIsFs(document.fullscreenElement === rootRef.current);
    document.addEventListener("fullscreenchange", on);
    return () => document.removeEventListener("fullscreenchange", on);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) await rootRef.current?.requestFullscreen?.();
      else await document.exitFullscreen?.();
    } catch (e) { /* ignore */ }
  };

  const downloadPdf = async () => {
    if (!printableRef.current || isPdfBusy) return;
    setIsPdfBusy(true);
    try {
      const { default: html2pdf } = await import("html2pdf.js");
      const safeName = (filename || masthead || "profile").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      await html2pdf()
        .set({
          margin:       [10, 10, 10, 10],
          filename:     `${safeName}.pdf`,
          image:        { type: "jpeg", quality: 0.95 },
          html2canvas:  { scale: 2, useCORS: true, backgroundColor: "#FAF6EC", scrollY: 0 },
          jsPDF:        { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak:    { mode: ["css", "legacy"] },
        })
        .from(printableRef.current)
        .save();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("PDF export failed", e);
    } finally { setIsPdfBusy(false); }
  };

  const replacePhoto = async (idx, file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Max 15 MB"); return; }
    setBusyIdx(idx);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = [...ePhotos];
      next[idx] = data.url;
      // Trim trailing empties but keep length up to 3
      setEPhotos(next);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusyIdx(-1);
      if (fileRefs[idx].current) fileRefs[idx].current.value = "";
    }
  };

  const cancelEdit = () => {
    setEMasthead(masthead); setEBrand(brand); setEYear(String(year || ""));
    setEPhotos(photos); setELeft(leftHtml); setERight(rightHtml);
    setEditing(false);
  };

  const saveChanges = async () => {
    if (!onSave) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave({
        masthead: eMasthead,
        brand: eBrand,
        year: eYear ? parseInt(eYear, 10) : null,
        top_photos: (ePhotos || []).filter(Boolean),
        col_left_html: eLeft,
        col_right_html: eRight,
      });
      toast.success("Profile updated");
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  // ------ RENDER PROPS ------
  const showPhotos    = editing ? ePhotos    : (photos || []).slice(0, 3);
  const showLeftHtml  = editing ? eLeft      : leftHtml;
  const showRightHtml = editing ? eRight     : rightHtml;
  const showMasthead  = editing ? eMasthead  : masthead;
  const showBrand     = editing ? eBrand     : brand;
  const showYear      = editing ? eYear      : year;

  return (
    <div
      ref={rootRef}
      style={{ background: "#FAF6EC", minHeight: "100vh" }}
      className={isFs ? "magazine-fullscreen" : ""}
      data-testid={testId}
    >
      {/* Toolbar row (outside the printable area) */}
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-6 flex flex-wrap gap-2 items-center justify-end">
        {canEdit && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-semibold border shadow-sm transition-transform hover:-translate-y-0.5"
            style={{ background: "#0F172A", color: "#F0C948", borderColor: "#0F172A" }}
            data-testid={`${testId}-edit-btn`}
          >
            <Edit3 className="w-3.5 h-3.5" /> Edit Profile
          </button>
        )}
        {editing && (
          <>
            <button
              type="button"
              onClick={saveChanges}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[12px] font-semibold shadow-sm transition-transform hover:-translate-y-0.5 disabled:opacity-60"
              style={{ background: "#059669", color: "#FFFFFF" }}
              data-testid={`${testId}-save-btn`}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {saving ? "Saving…" : "Save Changes"}
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-semibold border shadow-sm"
              style={{ background: "#FFFFFF", color: "#0F172A", borderColor: "#E7DCB9" }}
              data-testid={`${testId}-cancel-btn`}
            >
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
          </>
        )}
        <button
          type="button"
          onClick={downloadPdf}
          disabled={isPdfBusy}
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-semibold border shadow-sm transition-transform hover:-translate-y-0.5 disabled:opacity-60"
          style={{ background: "#FFFFFF", color: "#0F172A", borderColor: "#E7DCB9" }}
          data-testid={`${testId}-download-pdf`}
        >
          <Download className="w-3.5 h-3.5" />
          {isPdfBusy ? "Preparing…" : "Download PDF"}
        </button>
        <button
          type="button"
          onClick={toggleFullscreen}
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-semibold border shadow-sm transition-transform hover:-translate-y-0.5"
          style={{
            background: isFs ? "#0F172A" : "#FFFFFF",
            color: isFs ? "#F0C948" : "#0F172A",
            borderColor: "#E7DCB9",
          }}
          data-testid={`${testId}-fullscreen-btn`}
        >
          {isFs ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          {isFs ? "Exit Full Screen" : "View Full Screen"}
        </button>
      </div>

      {/* Editable masthead inputs (edit mode) */}
      {editing && (
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-4 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div className="md:col-span-2">
            <label className="text-[10px] tracking-[0.28em] font-bold uppercase" style={{ color: "#B98700" }}>Top-left name (masthead)</label>
            <input
              type="text"
              value={eMasthead}
              onChange={(e) => setEMasthead(e.target.value)}
              placeholder="Name shown in the masthead"
              className="mt-1 w-full px-3 py-2 rounded-lg border bg-white text-sm"
              style={{ borderColor: "#E7DCB9" }}
              data-testid={`${testId}-masthead-input`}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] tracking-[0.28em] font-bold uppercase" style={{ color: "#B98700" }}>Brand</label>
              <input
                type="text"
                value={eBrand}
                onChange={(e) => setEBrand(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border bg-white text-sm"
                style={{ borderColor: "#E7DCB9" }}
                data-testid={`${testId}-brand-input`}
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.28em] font-bold uppercase" style={{ color: "#B98700" }}>Year</label>
              <input
                type="text"
                value={eYear}
                onChange={(e) => setEYear(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border bg-white text-sm"
                style={{ borderColor: "#E7DCB9" }}
                data-testid={`${testId}-year-input`}
              />
            </div>
          </div>
        </div>
      )}

      {/* PRINTABLE AREA — everything below is captured in the PDF */}
      <div ref={printableRef}>
        {/* ==== MASTHEAD (name left · brand · year right) ==== */}
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-4 pb-2 flex flex-wrap items-baseline justify-between gap-3">
          <div
            className="text-[11px] tracking-[0.32em] font-bold uppercase"
            style={{ color: "#B98700" }}
            data-testid={`${testId}-masthead-left`}
          >
            {showMasthead || "\u00A0"}
          </div>
          <div
            className="text-[11px] tracking-[0.32em] font-bold uppercase"
            style={{ color: "#B98700" }}
            data-testid={`${testId}-masthead-right`}
          >
            {showBrand} · {showYear}
          </div>
        </div>

        {/* ==== TOP 3-IMAGE STRIP ==== */}
        {(showPhotos || []).slice(0, 3).length > 0 && (
          <div className="grid grid-cols-3 gap-1 max-w-[1200px] mx-auto pt-3 px-6 lg:px-10 magazine-photos">
            {[0, 1, 2].map((i) => {
              const src = (showPhotos || [])[i];
              return (
                <div key={i} className="relative aspect-[16/9] overflow-hidden rounded-sm group">
                  {src ? (
                    <img
                      src={resolveImageUrl(src)}
                      alt={`photo ${i + 1}`}
                      className="w-full h-full object-cover"
                      data-testid={`${testId}-photo-${i}`}
                      crossOrigin="anonymous"
                    />
                  ) : (
                    editing ? (
                      <div className="w-full h-full flex items-center justify-center bg-white/50 border border-dashed" style={{ borderColor: "#E7DCB9" }}>
                        <span className="text-[11px] text-slate-500">Add photo {i + 1}</span>
                      </div>
                    ) : null
                  )}

                  {editing && (
                    <>
                      <input
                        ref={fileRefs[i]}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => replacePhoto(i, e.target.files?.[0])}
                        data-testid={`${testId}-photo-${i}-input`}
                      />
                      <button
                        type="button"
                        onClick={() => fileRefs[i].current?.click()}
                        disabled={busyIdx === i}
                        className="absolute inset-0 flex items-center justify-center gap-2 bg-slate-900/55 text-white text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`${testId}-photo-${i}-replace`}
                      >
                        {busyIdx === i ? (
                          <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
                        ) : (
                          <><Upload className="w-4 h-4" /> {src ? `Replace image ${i + 1}` : `Add image ${i + 1}`}</>
                        )}
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Optional claim / notice slot */}
        {claimSlot && !editing && (
          <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-6">{claimSlot}</div>
        )}

        {/* ==== TWO-COLUMN MAGAZINE BODY ==== */}
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-10 pb-24 grid grid-cols-1 md:grid-cols-2 gap-12 magazine-body">
          {editing ? (
            <RichTextEditorPro
              value={eLeft}
              onChange={setELeft}
              minHeight="520px"
              testId={`${testId}-left-editor`}
            />
          ) : (
            <div
              className="prose-editorial"
              style={{ color: "#22201B" }}
              data-testid={`${testId}-left`}
              dangerouslySetInnerHTML={{ __html: (showLeftHtml || "").replace(/&nbsp;/g, " ").replace(/\u00A0/g, " ") }}
            />
          )}
          {editing ? (
            <RichTextEditorPro
              value={eRight}
              onChange={setERight}
              minHeight="520px"
              testId={`${testId}-right-editor`}
            />
          ) : (
            <div
              className="prose-editorial"
              style={{ color: "#22201B" }}
              data-testid={`${testId}-right`}
              dangerouslySetInnerHTML={{ __html: (showRightHtml || "").replace(/&nbsp;/g, " ").replace(/\u00A0/g, " ") }}
            />
          )}
        </div>
      </div>

      {/* Fullscreen tuning — larger typography, wider canvas, no page chrome bleed */}
      <style>{`
        .magazine-fullscreen { overflow-y: auto; }
        .magazine-fullscreen .magazine-photos { max-width: 1600px; }
        .magazine-fullscreen .magazine-body { max-width: 1600px; gap: 4rem; padding-top: 3.5rem; }
        .magazine-fullscreen .magazine-body .prose-editorial { font-size: 1.1rem; line-height: 1.85; }
        .magazine-fullscreen .magazine-body .prose-editorial h1 { font-size: 3.25rem; line-height: 1.05; }
        .magazine-fullscreen .magazine-body .prose-editorial h2 { font-size: 2.25rem; }
        .magazine-fullscreen .magazine-body .prose-editorial h3 { font-size: 1.6rem; }
      `}</style>
    </div>
  );
}

/**
 * True when the profile has enough magazine content to prefer the magazine layout
 * over the legacy pastel layout. If neither column has content, we render the
 * legacy view so older profiles are unaffected.
 */
export function hasMagazine(record) {
  if (!record) return false;
  const l = (record.col_left_html || "").trim();
  const r = (record.col_right_html || "").trim();
  const photos = (record.top_photos || []).filter(Boolean);
  return (l.length > 0 || r.length > 0) || photos.length >= 3;
}
